import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ChevronRight, ChevronLeft, ChevronDown, ChevronsLeft, ChevronsRight, Package, Loader2 } from 'lucide-react';
import { AvvisamiModal } from '../components/product/AvvisamiModal';
import { KitDetailModal } from '../components/product/KitDetailModal';
import { ProductSearchRow } from '../components/product/ProductSearchRow';
import { CompatModel } from '../components/product/CompatibilityChips';
import { SearchFilterBar } from '../components/search/SearchFilterBar';
import { useProductFacets, useOrigine, origineDaUrl, Origine } from '../hooks/useProductFacets';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/PortalContext';
import { useAuth } from '../context/AuthContext';
import { useMyStockAlerts } from '../hooks/useMyStockAlerts';
import { toast } from 'sonner';
import { Product } from '../types';
import { productService } from '../services/productService';
import api from '../services/api';
import { sortProducts } from '../utils/productSort';
import { matchesTipologia } from '../utils/tipologiaMatch';
import { usePageSize } from '../hooks/usePageSize';
import { contieneRicerca } from '../utils/ricerca';

interface ModelInfo {
  id: string;
  nome: string;
  marca: string;
  tipoMacchina: string;
  tipoMacchinaLabel: string;
}

const PAGE_SIZES = [25, 50, 100];


/** Numeri di pagina da mostrare nella paginazione (max `maxVisible` numeri
 * reali, il resto collassato in "..." — sempre visibili 1ª e ultima). */
function getPageNumbers(current: number, total: number, maxVisible = 6): (number | '...')[] {
  if (total <= maxVisible) return Array.from({ length: total }, (_, i) => i + 1);
  const windowSize = maxVisible - 2; // riserva 2 slot per 1ª e ultima pagina
  let start = Math.max(2, current - Math.floor(windowSize / 2));
  let end = start + windowSize - 1;
  if (end >= total) { end = total - 1; start = Math.max(2, end - windowSize + 1); }
  const result: (number | '...')[] = [1];
  if (start > 2) result.push('...');
  for (let p = start; p <= end; p++) result.push(p);
  if (end < total - 1) result.push('...');
  result.push(total);
  return result;
}

/**
 * Pagina di ricerca prodotti unificata — sostituisce le vecchie
 * ProductCategoryPage (/prodotti/:categoryId) e ModelResultsPage
 * (/consumabili-per/:modelId). Un'unica barra filtri orizzontale (Marca,
 * Tipologia, Originali/Compatibili, Modello, Cod. DTK/OEM) copre entrambe
 * le modalità:
 *
 * - Modalità categoria (`/prodotti/:categoryId`): il filtro Tipologia è
 *   precompilato dalla categoria in URL; la ricerca è server-side
 *   (`GET /api/products`), Cerca/Azzera navigano aggiornando l'URL.
 * - Modalità modello (`/consumabili-per/:modelId`): il filtro Modello è
 *   precompilato; il set di SKU compatibili viene caricato una volta
 *   dall'abbinatore, poi Marca/Tipologia/Origine/Codice filtrano
 *   client-side lo stesso set (Cerca applica i filtri correnti).
 */
export default function ProductSearchPage(): React.ReactElement {
  const { categoryId, modelId } = useParams<{ categoryId?: string; modelId?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addItem, items: cartItems, updateQty } = useCart();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { alerts, addAlert, isActive } = useMyStockAlerts();
  const getAlertQty = (sku: string) => alerts.find(a => a.sku === sku)?.qtyWanted ?? 0;
  const cartQty = (sku: string): number => cartItems.find(i => i.sku === sku)?.qty || 0;

  // ── Barra filtri (draft) ───────────────────────────────────────────
  const [marca, setMarca] = useState('');
  const [tipologia, setTipologia] = useState('');
  const [modelloId, setModelloId] = useState('');
  const [modelloLabel, setModelloLabel] = useState('');
  const [codice, setCodice] = useState('');
  // Filtri "applicati" client-side, usati solo in modalità modello (marca/
  // tipologia/origine/codice non sono parametri dell'endpoint SKU compatibili).
  const [appliedLocal, setAppliedLocal] = useState({ marca: '', tipologia: '', origine: '' as Origine, codice: '' });

  // L'azzeramento della tipologia non più coerente con la marca è nell'hook.
  const { facets, loadingFacets, origineOptions, tipologiaOptions, originiSelezione } =
    useProductFacets(marca, tipologia, { onTipologiaNonPiuValida: () => setTipologia('') });
  // Senza originali nella selezione passa da solo a "Solo compatibili".
  const { origine, scegliOrigine, allineaOrigine } = useOrigine(originiSelezione);

  // Pagina corrente riflessa nell'URL (?page=N) per link condivisibili e
  // avanti/indietro browser coerenti (richiesta utente 10/07/2026).
  const [page, setPage] = useState(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return p > 0 ? p : 1;
  });
  const [pageSize, setPageSize] = usePageSize('catalogo', PAGE_SIZES);
  const [stockWarnSku, setStockWarnSku] = useState<string | null>(null);
  const [rowQtys, setRowQtys] = useState<Record<string, number>>({});
  const [kitModalProduct, setKitModalProduct] = useState<Product | null>(null);
  const [avvisamiProduct, setAvvisamiProduct] = useState<Product | null>(null);
  const listSectionRef = useRef<HTMLDivElement>(null);
  const isFirstPageRender = useRef(true);

  const [model, setModel] = useState<ModelInfo | null>(null);
  const [modelNotFound, setModelNotFound] = useState(false);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  // Totale/pagine reali (modalità categoria) — la lista arriva già paginata
  // dal backend (`GET /api/products?page=&limit=`), non serve più scaricare
  // fino a 6000 righe e paginare lato client (fix perf 10/07/2026).
  const [serverTotal, setServerTotal] = useState(0);
  const [serverPages, setServerPages] = useState(1);
  const [skuModelli, setSkuModelli] = useState<Record<string, CompatModel[]>>({});
  // SKU con la sezione "Compatibilità" (sotto l'articolo) espansa — pilotata
  // dal bottone sotto "Cod. DTK" (vedi colonna Info più sotto).
  const [expandedCompatSkus, setExpandedCompatSkus] = useState<Set<string>>(new Set());
  const toggleCompat = (sku: string) => {
    setExpandedCompatSkus(prev => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku); else next.add(sku);
      return next;
    });
  };

  // Espansione elenco completo codici OEM (max 3 visibili in colonna,
  // stesso comportamento/stile del toggle compatibilità).
  const [expandedOemSkus, setExpandedOemSkus] = useState<Set<string>>(new Set());
  const toggleOem = (sku: string) => {
    setExpandedOemSkus(prev => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku); else next.add(sku);
      return next;
    });
  };

  // Ricorda l'ultima pagina di ricerca visitata (con filtri in
  // querystring), cosi il pulsante "Continua gli acquisti" nel carrello
  // può riportare l'utente esattamente qui invece che a una pagina di
  // catalogo generica (richiesta utente 24/02/2026).
  useEffect(() => {
    sessionStorage.setItem('dtk_last_search_url', window.location.pathname + window.location.search);
  }, [searchParams, categoryId, modelId]);

  // Scroll in cima alla lista al cambio pagina.
  useEffect(() => {
    if (isFirstPageRender.current) { isFirstPageRender.current = false; return; }
    const el = listSectionRef.current;
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 110;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }, [page]);

  // ── Sync draft filtri dall'URL quando cambia rotta ─────────────────
  useEffect(() => {
    if (modelId) {
      setModelloId(modelId);
      setMarca(''); setTipologia(''); scegliOrigine(''); setCodice('');
      setAppliedLocal({ marca: '', tipologia: '', origine: '', codice: '' });
    } else {
      setModelloId(''); setModelloLabel('');
      setMarca(searchParams.get('marca') || '');
      setTipologia(categoryId && categoryId !== 'tutti' ? categoryId : '');
      allineaOrigine(origineDaUrl(searchParams.get('origine')));
      setCodice(searchParams.get('q') || '');
    }
    const urlPage = parseInt(searchParams.get('page') || '1', 10);
    setPage(urlPage > 0 ? urlPage : 1);
    setExpandedCompatSkus(new Set());
    // "page" escluso di proposito dalle dipendenze: è l'UNICO param che
    // aggiorniamo noi stessi in URL (vedi `goToPage`) — includerlo
    // rimbalzerebbe l'utente a pagina 1 ad ogni click di paginazione.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, categoryId, searchParams.get('marca'), searchParams.get('origine'), searchParams.get('q')]);

  // ── Modalità modello: info modello + SKU compatibili ────────────────
  useEffect(() => {
    if (!modelId || authLoading) { if (!modelId) { setModel(null); setModelNotFound(false); } return; }
    let cancelled = false;
    setModelNotFound(false);
    setLoadingProducts(true);
    Promise.all([
      api.get<ModelInfo>(`/api/abbinatore/modello/${modelId}`),
      api.get<{ skus: string[]; compatibili: Record<string, CompatModel[]> }>(`/api/abbinatore/modello/${modelId}/skus`),
    ])
      .then(([mRes, sRes]) => {
        if (cancelled) return;
        setModel(mRes.data);
        // Solo il nome modello nel campo "Modello" (senza marca) — stessa
        // convenzione di AsyncModelSearchSelect.handleSelect (la marca è
        // già visibile nel sottotitolo pagina "Consumabili per {nome}" e
        // nel filtro "Marca" adiacente). Bug: la ricerca da navbar
        // popolava il campo con "MARCA Nome" invece del solo nome.
        setModelloLabel((mRes.data.nome || '').trim());
        setSkuModelli(sRes.data.compatibili || {});
        const skus = sRes.data.skus || [];
        if (skus.length === 0) { setAllProducts([]); setLoadingProducts(false); return; }
        productService.list({ skus: skus.join(','), limit: 500, publicOnly: true })
          .then(res => { if (!cancelled) setAllProducts(res.items); })
          .catch(() => { if (!cancelled) setAllProducts([]); })
          .finally(() => { if (!cancelled) setLoadingProducts(false); });
      })
      .catch(() => { if (!cancelled) { setModel(null); setModelNotFound(true); setAllProducts([]); setLoadingProducts(false); } });
    return () => { cancelled = true; };
  }, [modelId, authLoading]);

  // ── Modalità categoria: fetch server-side (marca/tipologia/origine/q,
  // paginazione reale page/limit — il backend ordina e pagina già in
  // MongoDB, fix perf 10/07/2026: prima si scaricavano fino a 6000 righe
  // e si paginava lato client). ──
  useEffect(() => {
    if (modelId || authLoading) return;
    let cancelled = false;
    setLoadingProducts(true);
    const marcaParam = searchParams.get('marca') || undefined;
    const origineParam = origineDaUrl(searchParams.get('origine'));
    const qParam = searchParams.get('q') || undefined;
    // La ricerca per Cod. DTK/OEM è un lookup puntuale (SKU/OEM) e deve
    // valere su tutto il catalogo: se applicassimo anche il filtro
    // Tipologia, un codice appartenente a una categoria diversa da quella
    // preselezionata risulterebbe "non trovato" pur essendo corretto.
    productService.list({
      category: !qParam && categoryId && categoryId !== 'tutti' ? categoryId : undefined,
      brand: marcaParam,
      type: origineParam === 'compatibile' ? 'Compatibile' : undefined,
      q: qParam,
      codeOnly: !!qParam,
      page,
      limit: pageSize,
      publicOnly: true,
    })
      .then(res => {
        if (cancelled) return;
        setAllProducts(res.items);
        setServerTotal(res.total);
        setServerPages(res.pages);
      })
      .catch(() => { if (!cancelled) { setAllProducts([]); setServerTotal(0); setServerPages(1); } })
      .finally(() => { if (!cancelled) setLoadingProducts(false); });
    return () => { cancelled = true; };
    // Dipendenze intenzionalmente puntuali sui singoli parametri URL letti
    // (`searchParams` cambia identità ad ogni render e causerebbe un loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, categoryId, searchParams.get('marca'), searchParams.get('origine'), searchParams.get('q'), page, pageSize, authLoading]);

  // Compatibilità in bulk (solo modalità categoria — in modalità modello
  // arrivano già dalla risposta `/modello/{id}/skus`).
  useEffect(() => {
    if (modelId) return;
    if (allProducts.length === 0) { setSkuModelli({}); return; }
    const skus = allProducts.map(p => p.sku);
    api.post<Record<string, CompatModel[]>>('/api/abbinatore/skus/modelli', { skus })
      .then(res => setSkuModelli(res.data))
      .catch(() => setSkuModelli({}));
  }, [modelId, allProducts]);

  // ── Filtri client-side (solo modalità modello) + ordinamento ────────
  const filtered = useMemo(() => {
    let list = allProducts;
    if (modelId) {
      list = list.filter(p => {
        if (appliedLocal.marca && p.brand !== appliedLocal.marca) return false;
        // La ricerca per codice è un lookup puntuale: ignora il filtro
        // Tipologia (un codice può appartenere a una categoria diversa da
        // quella selezionata, non deve risultare "non trovato" per questo).
        // Il filtro Tipologia lavora ora sui GRUPPI merceologici (30 valori,
        // stessa tassonomia della Ricerca avanzata) — `matchesTipologia`
        // accetta anche un match sul sottogruppo per restare compatibile
        // con i link legacy di navigazione (55 valori granulari).
        if (!appliedLocal.codice && !matchesTipologia(p, appliedLocal.tipologia)) return false;
        if (appliedLocal.origine === 'compatibile' && p.type !== 'Compatibile' && p.type !== 'Kit') return false;
        if (appliedLocal.codice) {
          const q = appliedLocal.codice;
          if (!contieneRicerca(p.sku, q) && !contieneRicerca(p.dtkCodice, q)) return false;
        }
        return true;
      });
    }
    return sortProducts(list);
  }, [allProducts, modelId, appliedLocal]);

  // Modalità modello: filtro+ordinamento client-side su tutti gli SKU
  // compatibili, poi slice per pagina. Modalità categoria: la lista arriva
  // già ordinata E paginata dal backend — `filtered` è solo un passaggio
  // di ri-ordinamento idempotente (stessa logica bucket Toner/640/resto
  // duplicata anche lato client, vedi `productSort.ts`), il totale/pagine
  // reali vengono dalla risposta server (`serverTotal`/`serverPages`).
  const totalCount = modelId ? filtered.length : serverTotal;
  const totalPages = modelId ? Math.max(1, Math.ceil(filtered.length / pageSize)) : Math.max(1, serverPages);
  const paginated = useMemo(
    () => (modelId ? filtered.slice((page - 1) * pageSize, page * pageSize) : filtered),
    [filtered, page, pageSize, modelId],
  );

  // Cambia pagina riflettendo il numero anche nell'URL (?page=N) — link
  // condivisibili e avanti/indietro browser coerenti.
  const goToPage = (p: number) => {
    const clamped = Math.max(1, Math.min(totalPages, p));
    setPage(clamped);
    const next = new URLSearchParams(searchParams);
    if (clamped > 1) next.set('page', String(clamped));
    else next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const isDirty = !!(marca || tipologia || origine || modelloId || codice);

  const handleCerca = (codiceOverride?: string) => {
    const effectiveCodice = codiceOverride ?? codice;
    if (modelloId && modelloId !== modelId) {
      navigate(`/consumabili-per/${modelloId}`);
      return;
    }
    if (modelId) {
      setAppliedLocal({ marca, tipologia, origine, codice: effectiveCodice });
      goToPage(1);
      return;
    }
    const params = new URLSearchParams();
    if (marca) params.set('marca', marca);
    if (origine) params.set('origine', origine);
    if (effectiveCodice.trim()) params.set('q', effectiveCodice.trim());
    // Se si cerca per Cod. DTK/OEM, la Tipologia preselezionata va ignorata
    // (il codice potrebbe appartenere a una categoria diversa) — la ricerca
    // per codice naviga sempre su "tutti" per restare coerente con il
    // risultato effettivo mostrato (nessun filtro Tipologia fantasma).
    const targetCategory = effectiveCodice.trim() ? 'tutti' : (tipologia || 'tutti');
    navigate(`/prodotti/${targetCategory}${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const handleAzzera = () => {
    setMarca(''); setTipologia(''); scegliOrigine(''); setModelloId(''); setModelloLabel(''); setCodice('');
    if (modelId) {
      setAppliedLocal({ marca: '', tipologia: '', origine: '', codice: '' });
      goToPage(1);
    } else {
      navigate('/prodotti/tutti');
    }
  };

  const handleAddToCart = (e: React.MouseEvent, product: Product) => {
    e.preventDefault();
    e.stopPropagation();
    const qty = rowQtys[product.sku] || 1;
    const pStock = product.stock ?? 0;
    if (pStock > 0 && qty > pStock) { setStockWarnSku(product.sku); return; }
    // Se la disponibilità basta solo in parte, l'avviso lo dà ``addItem``.
    if (addItem(product, qty) === qty) toast.success(`${product.sku} x${qty} aggiunto al carrello`);
  };

  const pageTitle = modelId
    ? (model ? `Consumabili per ${model.marca} - ${model.nome}` : 'Ricerca per modello')
    : 'Catalogo Prodotti';
  const detailHref = (p: Product) => modelId ? `/prodotti/${p.category}/${p.sku}?from_model=${modelId}` : `/prodotti/${categoryId || p.category}/${p.sku}`;

  if (modelId && modelNotFound) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-20 text-center">
        <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h1 className="text-xl font-semibold">Modello non trovato</h1>
        <button onClick={() => navigate(-1)} className="mt-5 text-sm text-[hsl(214,58%,50%)] hover:underline">Torna indietro</button>
      </div>
    );
  }

  // Utenti non loggati: il catalogo/ricerca reale è ora fruibile anche a chi
  // non è autenticato (richiesta utente 15/02/2026 — prima veniva mostrata
  // qui la pagina istituzionale "I Nostri Prodotti", ora spostata su un
  // indirizzo dedicato /i-nostri-prodotti, vedi GuestCategoriesOverview.tsx
  // e CategoryInfoPage.tsx). I prezzi/azioni d'acquisto restano gestiti da
  // `isAuthenticated` dentro ProductSearchRow (LoginToBuy per i non loggati).

  return (
    <div data-testid="product-search-page">
      {/* Barra bianca superiore — prima ospitava il breadcrumb "Home > ...",
          ora mostra direttamente il titolo pagina a sinistra (font compatto,
          coerente con lo stile già usato in CategoryInfoPage.tsx) e il
          selettore "Mostra N per pagina" a destra (richiesta utente
          26/02/2026). Per la ricerca per modello la marca è inclusa nel
          titolo stesso ("Consumabili per RICOH - AFICIO MP C 3003 SP"),
          non più come label separata sopra. Al centro il numero dei
          risultati, che prima stava nella barra filtri (richiesta utente
          05/10/2026). */}
      <div className="border-b bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-x-4 gap-y-1 flex-wrap sm:grid sm:grid-cols-[1fr_auto_1fr]">
          <h1 className="text-lg font-semibold min-w-0" data-testid="product-search-page-title">{pageTitle}</h1>
          <p className="text-sm text-muted-foreground whitespace-nowrap order-last w-full sm:order-none sm:w-auto" aria-live="polite" data-testid="product-search-result-count">
            {!loadingProducts && totalCount > 0 && (
              <><span className="font-semibold text-foreground">{totalCount.toLocaleString('it-IT')}</span> {totalCount === 1 ? 'risultato' : 'risultati'}</>
            )}
          </p>
          {!loadingProducts && totalCount > 0 && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground flex-shrink-0 sm:justify-self-end">
              <span>Mostra</span>
              <div className="relative">
                <select
                  value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); goToPage(1); }}
                  className="appearance-none h-8 pl-2.5 pr-7 rounded-lg border bg-white text-xs focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/20 cursor-pointer"
                  data-testid="product-search-page-size"
                >
                  {PAGE_SIZES.map(s => <option key={s} value={s}>{s} per pagina</option>)}
                </select>
                <ChevronDown className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        {/* Barra filtri orizzontale */}
        <SearchFilterBar
          marca={marca} onMarcaChange={v => { setMarca(v); setModelloId(''); setModelloLabel(''); }}
          tipologia={tipologia} onTipologiaChange={setTipologia}
          origine={origine} onOrigineChange={scegliOrigine}
          facets={facets} loadingFacets={loadingFacets} origineOptions={origineOptions} tipologiaOptions={tipologiaOptions}
          modello={{ id: modelloId, label: modelloLabel, onChange: (id, label) => { setModelloId(id); setModelloLabel(label); } }}
          codice={{ value: codice, onChange: setCodice }}
          onSearch={handleCerca}
          onReset={handleAzzera}
          isDirty={isDirty}
          testidPrefix="product-search-filter"
        />

        <div ref={listSectionRef}>
          {loadingProducts ? (
            <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Caricamento prodotti…</span>
            </div>
          ) : totalCount === 0 ? (
            <div className="rounded-xl border bg-white p-12 text-center shadow-sm" data-testid="product-search-empty">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-sm text-muted-foreground mb-1">Nessun prodotto trovato con i filtri selezionati.</p>
              <button onClick={handleAzzera} className="text-sm text-[hsl(214,58%,50%)] hover:underline">Azzera filtri</button>
            </div>
          ) : (
            <>
              {/* overflow-hidden rimosso dal wrapper esterno (causava il
                  troncamento del popover sconto-scatola per la riga in
                  cima alla lista, che apre verso l'alto — bug segnalato
                  10/07/2026). Gli angoli arrotondati si ottengono ora
                  applicando rounded-t-xl/rounded-b-xl alla 1ª/ultima riga. */}
              <div className="rounded-xl border bg-white shadow-sm">
                <div className="divide-y" data-testid="products-table">
                  {paginated.map((p, idx) => {
                    const pStock = p.stock ?? 0;
                    const rowQty = rowQtys[p.sku] || 1;
                    const cQty = cartQty(p.sku);

                    return (
                      <ProductSearchRow
                        key={p.sku}
                        product={p}
                        isFirstRow={idx === 0}
                        isLastRow={idx === paginated.length - 1}
                        detailHref={detailHref(p)}
                        isAuthenticated={isAuthenticated}
                        compatModels={skuModelli[p.sku]}
                        searchedModel={model ? { id: model.id, nome: model.nome, marca: model.marca } : undefined}
                        searchedCode={searchParams.get('q') || undefined}
                        isCompatExpanded={expandedCompatSkus.has(p.sku)}
                        onToggleCompat={() => toggleCompat(p.sku)}
                        isOemExpanded={expandedOemSkus.has(p.sku)}
                        onToggleOem={() => toggleOem(p.sku)}
                        onOpenKit={() => setKitModalProduct(p)}
                        isFav={isFavorite(p.sku)}
                        onToggleFavorite={() => { toggleFavorite(p); toast.success(isFavorite(p.sku) ? 'Rimosso dai preferiti' : 'Aggiunto ai preferiti'); }}
                        alertActive={isActive(p.sku)}
                        alertQty={getAlertQty(p.sku)}
                        onOpenAvvisami={() => setAvvisamiProduct(p)}
                        rowQty={rowQty}
                        onRowQtyChange={(v) => setRowQtys(prev => ({ ...prev, [p.sku]: v }))}
                        onAddToCart={(e) => handleAddToCart(e, p)}
                        showStockWarningInline={stockWarnSku === p.sku}
                        onOrderAvailableInline={() => {
                          const added = addItem(p, pStock);
                          const missing = rowQty - added;
                          addAlert({ sku: p.sku, title: p.title, brand: p.brand, category: p.category, qtyWanted: missing }).catch(() => {});
                          toast.success(`${added} pz ordinati + alert per i restanti ${missing}`);
                          setStockWarnSku(null);
                        }}
                        onAddAvailableOnlyInline={() => {
                          const added = addItem(p, pStock);
                          if (added > 0) toast.success(`${added} pz aggiunti al carrello`);
                          setStockWarnSku(null);
                        }}
                        cartQty={cQty}
                        onUpdateCartQty={(v) => { updateQty(p.sku, v); if (v > pStock && pStock > 0) setStockWarnSku(p.sku); }}
                        showStockOptionsButton={cQty > pStock && pStock > 0}
                        showStockWarningCart={stockWarnSku === p.sku && pStock > 0 && cQty > pStock}
                        onToggleStockWarningCart={() => setStockWarnSku(stockWarnSku === p.sku ? null : p.sku)}
                        onOrderAvailableCart={() => {
                          const missing = cQty - pStock;
                          updateQty(p.sku, pStock);
                          addAlert({ sku: p.sku, title: p.title, brand: p.brand, category: p.category, qtyWanted: missing }).catch(() => {});
                          toast.success(`${pStock} pz ordinati + alert per i restanti ${missing}`);
                          setStockWarnSku(null);
                        }}
                        onAddAvailableOnlyCart={() => {
                          updateQty(p.sku, pStock);
                          toast.success(`Quantità aggiornata a ${pStock} pz disponibili`);
                          setStockWarnSku(null);
                        }}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Paginazione */}
              {totalCount > pageSize && (
                <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm" data-testid="pagination-controls">
                  <span className="text-xs text-muted-foreground" data-testid="pagination-info">
                    {(page - 1) * pageSize + 1}{'\u2013'}{Math.min(page * pageSize, totalCount)} di {totalCount}
                  </span>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => goToPage(1)} disabled={page === 1}
                      data-testid="pagination-first" title="Prima pagina" aria-label="Prima pagina"
                      className="h-8 w-8 rounded-lg border bg-white text-xs font-medium hover:bg-secondary/60 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center">
                      <ChevronsLeft className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => goToPage(page - 1)} disabled={page === 1}
                      data-testid="pagination-prev" className="h-8 px-3 rounded-lg border bg-white text-xs font-medium hover:bg-secondary/60 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1">
                      <ChevronLeft className="w-3.5 h-3.5" /> Precedente
                    </button>
                    <div className="hidden sm:flex items-center gap-1" data-testid="pagination-numbers">
                      {getPageNumbers(page, totalPages).map((n, i) => n === '...' ? (
                        <span key={`ellipsis-${i}`} className="px-1 text-xs text-muted-foreground select-none">&hellip;</span>
                      ) : (
                        <button
                          key={n}
                          type="button"
                          onClick={() => goToPage(n)}
                          data-testid={`pagination-page-${n}`}
                          aria-current={n === page ? 'page' : undefined}
                          className={`h-8 w-8 rounded-lg text-xs font-medium transition-colors ${n === page ? 'bg-[hsl(214,58%,41%)] text-white' : 'border bg-white hover:bg-secondary/60'}`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                    <span className="sm:hidden text-xs font-medium tabular-nums px-2" data-testid="pagination-current">Pagina {page} di {totalPages}</span>
                    <button type="button" onClick={() => goToPage(page + 1)} disabled={page >= totalPages}
                      data-testid="pagination-next" className="h-8 px-3 rounded-lg border bg-white text-xs font-medium hover:bg-secondary/60 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1">
                      Successiva <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => goToPage(totalPages)} disabled={page >= totalPages}
                      data-testid="pagination-last" title="Ultima pagina" aria-label="Ultima pagina"
                      className="h-8 w-8 rounded-lg border bg-white text-xs font-medium hover:bg-secondary/60 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center">
                      <ChevronsRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Kit Detail Modal */}
      <KitDetailModal
        open={!!kitModalProduct}
        onClose={() => setKitModalProduct(null)}
        productSku={kitModalProduct?.sku || ''}
        productTitle={kitModalProduct?.title || ''}
        items={kitModalProduct?.kitItems || []}
      />

      {/* Modal Avvisami */}
      {avvisamiProduct && (
        <AvvisamiModal
          product={avvisamiProduct}
          cartQty={cartQty(avvisamiProduct.sku)}
          isAlreadyActive={isActive(avvisamiProduct.sku)}
          onSave={async (qty) => {
            await addAlert({ sku: avvisamiProduct.sku, title: avvisamiProduct.title, brand: avvisamiProduct.brand, category: avvisamiProduct.category, qtyWanted: qty });
            toast.success(`Avviso salvato per ${avvisamiProduct.sku}`);
          }}
          onClose={() => setAvvisamiProduct(null)}
        />
      )}
    </div>
  );
}
